import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

const Contact = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // バックエンドAPI URL
      const apiUrl = import.meta.env.VITE_API_URL || 
        (window.location.hostname === 'smartmates.org' || window.location.hostname.includes('smartmates.org')
          ? 'https://sales-ai-serve-production.up.railway.app'
          : window.location.hostname.includes('railway.app')
          ? window.location.origin.replace('sales-ai-hp', 'sales-ai-serve')
          : 'http://localhost:8080');
      
      // お問い合わせ送信API（実装が必要な場合は後で追加）
      // 現在はフォームデータをコンソールに出力
      console.log('Contact form data:', formData);
      
      // 成功をシミュレート
      setTimeout(() => {
        setSubmitStatus('success');
        setIsSubmitting(false);
        setFormData({
          company: '',
          name: '',
          email: '',
          phone: '',
          message: ''
        });
      }, 1000);
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setSubmitStatus('error');
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-32 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-8 tracking-tight">
            {t('contact.title')}
          </h2>
          <p className="text-xl md:text-2xl font-bold text-blue-300 mb-4">
            {t('contact.subtitle')}
          </p>
        </motion.div>

        <motion.form
          onSubmit={handleSubmit}
          className="bg-white rounded-3xl p-8 md:p-12 shadow-2xl"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="space-y-6">
            <div>
              <label htmlFor="company" className="block text-lg font-bold text-gray-900 mb-2">
                {t('contact.form.company')} <span className="text-red-500">{t('trialSignup.required')}</span>
              </label>
              <input
                type="text"
                id="company"
                required
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                className="w-full px-6 py-4 rounded-xl border-2 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-lg font-semibold"
                placeholder={t('contact.form.company')}
              />
            </div>

            <div>
              <label htmlFor="name" className="block text-lg font-bold text-gray-900 mb-2">
                {t('contact.form.name')} <span className="text-red-500">{t('trialSignup.required')}</span>
              </label>
              <input
                type="text"
                id="name"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-6 py-4 rounded-xl border-2 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-lg font-semibold"
                placeholder={t('contact.form.name')}
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-lg font-bold text-gray-900 mb-2">
                {t('contact.form.email')} <span className="text-red-500">{t('trialSignup.required')}</span>
              </label>
              <input
                type="email"
                id="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-6 py-4 rounded-xl border-2 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-lg font-semibold"
                placeholder={t('contact.form.email')}
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-lg font-bold text-gray-900 mb-2">
                {t('contact.form.phone')}
              </label>
              <input
                type="tel"
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-6 py-4 rounded-xl border-2 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-lg font-semibold"
                placeholder={t('contact.form.phone')}
              />
            </div>

            <div>
              <label htmlFor="message" className="block text-lg font-bold text-gray-900 mb-2">
                {t('contact.form.message')} <span className="text-red-500">{t('trialSignup.required')}</span>
              </label>
              <textarea
                id="message"
                required
                rows={6}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full px-6 py-4 rounded-xl border-2 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-lg font-semibold resize-none"
                placeholder={t('contact.form.message')}
              />
            </div>
          </div>

          {submitStatus === 'success' && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6 p-4 bg-green-50 border-2 border-green-500 rounded-xl text-green-800 font-semibold"
            >
              {t('contact.form.success')}
            </motion.div>
          )}

          {submitStatus === 'error' && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6 p-4 bg-red-50 border-2 border-red-500 rounded-xl text-red-800 font-semibold"
            >
              {t('contact.form.error')}
            </motion.div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full mt-10 bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-12 py-6 rounded-2xl text-2xl font-black hover:from-blue-600 hover:to-cyan-600 transition-all hover:scale-105 shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? t('contact.form.submitting') : t('contact.form.submit')}
          </button>
        </motion.form>
      </div>
    </section>
  );
};

export default Contact;

